/**
 * Shared Virtual Visit experience — client share, listing site, desk and the
 * standalone /v/:visitId page all render this one component.
 *
 * Chrome: WebGL2 check, loading progress bar, fallback message, room chips,
 * floor selector, floor-plan minimap with room hotspots, controls hint,
 * fullscreen (native where allowed, CSS on iPhone Safari), and optional
 * agent branding (never in unbranded/MLS mode).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { detectWebGL2, floorLabel, roomsOnFloor } from "@/lib/visitManifest";
import BricksLoader from "@/components/BricksLoader";

const C = {
  brick: "#D2542B",
  paper: "#F4F1EB",
  night: "#0b0a09",
  glass: "rgba(20,18,16,0.72)",
  hair: "rgba(244,241,235,0.16)",
  mute: "rgba(244,241,235,0.66)",
  font: '"General Sans",ui-sans-serif,-apple-system,BlinkMacSystemFont,sans-serif',
  mono: '"IoskeleyMono",ui-monospace,"SF Mono",Menlo,monospace',
};

const chip = (active) => ({
  flex: "0 0 auto",
  fontFamily: C.font,
  fontSize: 12,
  fontWeight: 500,
  padding: "0 14px",
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  border: `1px solid ${active ? C.brick : C.hair}`,
  background: active ? C.brick : C.glass,
  color: active ? "#fff" : C.paper,
  cursor: "pointer",
  whiteSpace: "nowrap",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
});

const iconBtn = {
  width: 44,
  height: 44,
  borderRadius: 999,
  border: `1px solid ${C.hair}`,
  background: C.glass,
  color: C.paper,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  fontSize: 15,
  lineHeight: 1,
};

function FloorPlan({ manifest, floor, activeId, loaded, onPick }) {
  const plan = (manifest.floor_plans || []).find((f) => Number(f.floor_index || 0) === floor);
  const rooms = roomsOnFloor(manifest, floor).filter((r) => r.hotspot);
  if (!plan && !rooms.length) return null;
  return (
    <div
      data-testid="visit-floorplan"
      style={{
        position: "absolute",
        left: 12,
        bottom: 58,
        width: "min(32%, 190px)",
        aspectRatio: "4 / 3",
        background: plan ? `${C.glass} url("${plan.image_url}") center/contain no-repeat` : C.glass,
        border: `1px solid ${C.hair}`,
        borderRadius: 8,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        overflow: "hidden",
      }}
    >
      {rooms.map((r) => (
        <button
          key={r.id}
          type="button"
          title={r.name}
          aria-label={`Go to ${r.name}`}
          onClick={() => onPick(r.id)}
          style={{
            position: "absolute",
            left: `${r.hotspot.x * 100}%`,
            top: `${r.hotspot.y * 100}%`,
            transform: "translate(-50%,-50%)",
            width: r.id === activeId ? 16 : 12,
            height: r.id === activeId ? 16 : 12,
            borderRadius: 999,
            border: "2px solid #fff",
            background: r.id === activeId ? C.brick : loaded.has(r.id) ? "rgba(210,84,43,0.55)" : "rgba(244,241,235,0.35)",
            padding: 0,
            cursor: "pointer",
            boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
          }}
        />
      ))}
    </div>
  );
}

function Branding({ branding }) {
  if (!branding) return null;
  const bits = [branding.agent_name, branding.brokerage_name].filter(Boolean).join(" · ");
  const contact = [branding.agent_phone, branding.agent_email].filter(Boolean);
  if (!bits && !contact.length) return null;
  return (
    <div
      data-testid="visit-branding"
      style={{
        position: "absolute",
        right: 12,
        top: 12,
        maxWidth: "min(42%, 320px)",
        overflowWrap: "anywhere",
        padding: "8px 11px",
        borderRadius: 8,
        background: C.glass,
        border: `1px solid ${C.hair}`,
        color: C.paper,
        fontFamily: C.font,
        fontSize: 12,
        lineHeight: 1.4,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        textAlign: "right",
      }}
    >
      {bits ? <div style={{ fontWeight: 600 }}>{bits}</div> : null}
      {contact.map((c) => (
        <div key={c} style={{ color: C.mute }}>
          {c.includes("@") ? <a href={`mailto:${c}`} style={{ color: C.mute }}>{c}</a> : <a href={`tel:${c.replace(/[^+\d]/g, "")}`} style={{ color: C.mute }}>{c}</a>}
        </div>
      ))}
    </div>
  );
}

export default function VisitExperience({ manifest, variant = "inline", fallbackUrl = null, embed = false, showTitle = false }) {
  const hostRef = useRef(null);
  const stageRef = useRef(null);
  const engineRef = useRef(null);
  const [status, setStatus] = useState("checking"); // checking|loading|ready|error|nowebgl
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState(null);
  const [loaded, setLoaded] = useState(() => new Set());
  const [floor, setFloor] = useState(manifest?.rooms?.[0]?.floor_index ?? 0);
  const [hint, setHint] = useState(true);
  const [cssFull, setCssFull] = useState(false);
  const [splats, setSplats] = useState(0);
  const [failed, setFailed] = useState(() => new Set());
  const multiRoom = (manifest?.rooms?.length || 0) > 1;
  const manifestKey = useMemo(
    () => (manifest ? manifest.rooms.map((r) => r.id + "|" + r.url.split("?")[0]).join(",") + (manifest.base ? "|b" : "") : ""),
    [manifest]
  );

  useEffect(() => {
    if (!manifest || !stageRef.current) return undefined;
    if (!detectWebGL2()) {
      setStatus("nowebgl");
      return undefined;
    }
    let cancelled = false;
    setStatus("loading");
    setProgress(0);
    setLoaded(new Set());
    setFailed(new Set());
    const startId = manifest.base ? "base" : manifest.start_room_id;
    import("./splatEngine")
      .then(({ createSplatEngine }) =>
        createSplatEngine(stageRef.current, manifest, {
          onProgress: (id, frac) => {
            if (cancelled || id !== (engineRef.current?.activeId ?? startId)) return;
            if (frac != null) setProgress(frac);
          },
          onActiveRoom: (id) => {
            if (cancelled) return;
            setActiveId(id);
            const r = manifest.rooms.find((x) => x.id === id);
            if (r) setFloor(r.floor_index);
          },
          onRoomLoaded: (id) => !cancelled && setLoaded((s) => new Set(s).add(id)),
          onRoomError: (id) => !cancelled && setFailed((s) => new Set(s).add(id)),
          onReady: ({ numSplats }) => {
            if (cancelled) return;
            setSplats(numSplats);
            setProgress(1);
            setStatus("ready");
          },
          onError: () => !cancelled && setStatus("error"),
        })
      )
      .then((engine) => {
        if (cancelled) {
          engine.dispose();
          return;
        }
        engineRef.current = engine;
      })
      .catch(() => !cancelled && setStatus("error"));
    return () => {
      cancelled = true;
      if (engineRef.current) {
        engineRef.current.dispose();
        engineRef.current = null;
      }
    };
    // manifestKey captures the asset identity; signed-URL churn must not reload.
  }, [manifestKey]);

  useEffect(() => {
    if (status !== "ready" || !hint) return undefined;
    const t = setTimeout(() => setHint(false), 7000);
    const host = stageRef.current;
    const off = () => setHint(false);
    host && host.addEventListener("pointerdown", off, { once: true });
    return () => {
      clearTimeout(t);
      host && host.removeEventListener("pointerdown", off);
    };
  }, [status, hint]);

  const pickRoom = useCallback((id) => {
    setHint(false);
    engineRef.current && engineRef.current.showRoom(id, { fly: true });
  }, []);

  const toggleFull = useCallback(() => {
    const el = hostRef.current;
    if (!el) return;
    const doc = document;
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      (doc.exitFullscreen || doc.webkitExitFullscreen).call(doc);
      return;
    }
    if (cssFull) {
      setCssFull(false);
      return;
    }
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      Promise.resolve(req.call(el)).catch(() => setCssFull(true));
    } else {
      setCssFull(true); // iPhone Safari: no element fullscreen
    }
  }, [cssFull]);

  if (!manifest) return null;
  const fill = variant === "page" || embed;
  const floorRooms = roomsOnFloor(manifest, floor);
  const pct = Math.round(progress * 100);
  const activeRoom = manifest.rooms.find((r) => r.id === activeId);

  const outer = cssFull
    ? { position: "fixed", inset: 0, zIndex: 2147483000, background: C.night }
    : fill
      ? { position: "relative", width: "100%", height: "100%", background: C.night }
      : { position: "relative", width: "100%", paddingBottom: "56.25%", background: C.night, borderRadius: 2, overflow: "hidden" };

  return (
    <div
      ref={hostRef}
      data-testid="visit-experience"
      data-visit-status={status}
      data-visit-splats={splats || undefined}
      data-visit-active-room={activeId || undefined}
      style={outer}
    >
      <div ref={stageRef} style={{ position: "absolute", inset: 0 }} />

      {/* progress bar */}
      {(status === "loading" || status === "checking") && (
        <>
          <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: 3, background: "rgba(244,241,235,0.08)" }}>
            <div data-testid="visit-progress" style={{ width: `${Math.max(4, pct)}%`, height: "100%", background: C.brick, transition: "width 160ms linear" }} />
          </div>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, pointerEvents: "none", color: C.paper, fontFamily: C.font }}>
            <BricksLoader variant="assemble" size={40} color={C.brick} />
            <div style={{ fontSize: 13 }}>Loading Virtual Visit{pct ? ` · ${pct}%` : "…"}</div>
          </div>
        </>
      )}

      {/* fallback */}
      {(status === "error" || status === "nowebgl") && (
        <div data-testid="visit-fallback" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 'var(--bx-pad)', textAlign: "center", color: C.paper, fontFamily: C.font }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>
            {status === "nowebgl" ? "This browser can't show the 3D Visit" : "The Virtual Visit didn't load"}
          </div>
          <div style={{ fontSize: 13, color: C.mute, maxWidth: 420, lineHeight: 1.5 }}>
            {status === "nowebgl"
              ? "It needs WebGL 2. Try the latest Safari or Chrome, or turn off Low Power Mode / hardware-acceleration blocks."
              : "Check your connection and try again."}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            {status === "error" && (
              <button type="button" onClick={() => window.location.reload()} style={{ ...chip(true), fontSize: 13 }}>
                Try again
              </button>
            )}
            {fallbackUrl ? (
              <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" style={{ ...chip(false), fontSize: 13, textDecoration: "none" }}>
                Open file
              </a>
            ) : null}
          </div>
        </div>
      )}

      {status === "ready" && (
        <>
          {showTitle && (manifest.address || manifest.title) ? (
            <div style={{ position: "absolute", left: 12, top: 12, maxWidth: "50%", padding: "8px 11px", borderRadius: 8, background: C.glass, border: `1px solid ${C.hair}`, color: C.paper, fontFamily: C.font, fontSize: 13, fontWeight: 500, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
              {manifest.address || manifest.title}
              {activeRoom && multiRoom ? <span style={{ color: C.mute, fontWeight: 400 }}> · {activeRoom.name}</span> : null}
            </div>
          ) : null}

          {!manifest.unbranded && <Branding branding={manifest.branding} />}

          {multiRoom && (
            <FloorPlan manifest={manifest} floor={floor} activeId={activeId} loaded={loaded} onPick={pickRoom} />
          )}

          {/* bottom bar: floors + rooms + actions */}
          <div style={{ position: "absolute", left: 12, right: 12, bottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
              {manifest.floors.length > 1 &&
                manifest.floors.map((f) => (
                  <button key={`f${f}`} type="button" onClick={() => setFloor(f)} style={{ ...chip(f === floor), fontFamily: C.mono, fontSize: 11 }}>
                    {floorLabel(f)}
                  </button>
                ))}
              {multiRoom &&
                floorRooms.map((r) => (
                  <button key={r.id} type="button" data-testid="visit-room-chip" onClick={() => pickRoom(r.id)} style={chip(r.id === activeId)}>
                    {r.name}
                    {failed.has(r.id) ? <span style={{ opacity: 0.8 }}> ⚠</span> : !loaded.has(r.id) && r.id !== activeId ? <span style={{ opacity: 0.55 }}> ·</span> : null}
                  </button>
                ))}
            </div>
            <button type="button" aria-label="Reset view" title="Reset view" style={iconBtn} onClick={() => engineRef.current && engineRef.current.resetView()}>
              ⟲
            </button>
            <button type="button" aria-label="Full screen" title="Full screen" style={iconBtn} onClick={toggleFull}>
              {cssFull ? "✕" : "⤢"}
            </button>
          </div>

          {activeId && failed.has(activeId) && (
            <div role="status" data-testid="visit-room-error" style={{ position: "absolute", left: "50%", top: 60, transform: "translateX(-50%)", padding: "8px 12px", borderRadius: 8, background: "rgba(140,47,36,0.9)", color: "#fff", fontFamily: C.font, fontSize: 12 }}>
              {(manifest.rooms.find((r) => r.id === activeId) || {}).name || "This room"} didn't load. Pick another room.
            </div>
          )}

          {hint && (
            <button
              type="button"
              onClick={() => setHint(false)}
              style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", maxWidth: "86%", whiteSpace: "normal", textAlign: "center", padding: "7px 12px", borderRadius: 999, border: `1px solid ${C.hair}`, background: C.glass, color: C.paper, fontFamily: C.font, fontSize: 12, cursor: "pointer", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            >
              <span className="visit-hint-touch">Drag to look · pinch to zoom · two fingers to move</span>
              <span className="visit-hint-mouse">Drag to look · scroll to zoom · WASD to walk</span>
            </button>
          )}
        </>
      )}
      <style>{`
        .visit-hint-touch { display: none }
        @media (pointer: coarse) { .visit-hint-touch { display: inline } .visit-hint-mouse { display: none } }
      `}</style>
    </div>
  );
}
